import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { PersistGate } from 'redux-persist/integration/react';
import { Route, useHistory, Switch, Redirect } from 'react-router-dom';
import { Security, SecureRoute, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';

// PAGES
import Overview from './pages/Overview';
import SectionDetail from './pages/SectionDetail';
import Notes from './pages/Notes';
import ScratchPad from './pages/ScratchPad';
import Login from './pages/Login';
import Glance from './pages/Glance';
import DataManagement from './pages/DataManagement';
import NoAccess from './pages/NoAcess';

// COMPONENTS
import Initialize from './Initialize';
import Navigation from './components/Navigation';

// LocalStorage Checker
// import LocalStorageChecker from './components/LocalStorageChecker';

// CONFIGS
import { getOktaAuthConfig } from './config';
import { store, persistor } from './redux/store';

// HELPERS
// import { tokenHasExpired } from './helpers/auth';
import { fetchCall } from './services/api';
import * as ConfigActions from './redux/actions/config';
import { configureGATrackingIDsForEnv } from './helpers/googleAnalytics';
import { ReduxStates } from './redux/reduxTypes';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUnreadNotesCount } from './pages/Notes/helpers';
import { setIntervalId, setUnreadNotes } from './redux/actions/data';

let oktaAuth: OktaAuth;

const AppRouter = (): React.ReactElement => {
  const history = useHistory();
  const [hasConfig, setHasConfig] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [renewed, setRenewed] = useState(false);
  const user = useSelector((state: ReduxStates) => state.user);
  const client = useSelector((state: ReduxStates) => state.client);
  const data = useSelector((state: ReduxStates) => state.data);
  const dispatch = useDispatch();

  const authHandler = () => {
    history.push('/');
  };

  useEffect(() => {
    const intervalCall = setInterval(() => {
      fetchUnreadNotesCount()
        .then((data) => {
          dispatch(setUnreadNotes(data.newMsgsCount));
        })
        .catch((error) => {
          console.log('error fetching unread notes count:', error);
        });
    }, 60000 * 30);
    dispatch(setIntervalId(intervalCall));
  }, []);

  // Triggered when a token has been expired
  oktaAuth?.tokenManager.on('expired', async () => {
    console.log('Token expired. Removing token from cache and refreshing');
    await oktaAuth.tokenManager.clear();
    window.location.reload();
  });

  // Triggered when a token has been renewed
  oktaAuth?.tokenManager.on('renewed', () => {
    setRenewed(true);
  });

  const restoreOriginalUri = async (_oktaAuth: any, originalUri: any) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  useEffect(() => {
    if (hasConfig) {
      fetchClientId()
        .then((clientId) => {
          const oktaAuthConfig = getOktaAuthConfig(store.getState().config.react_app_okta_url);
          oktaAuth = new OktaAuth({ ...oktaAuthConfig, clientId });
          setAuthenticated(true);
        })
        .catch((error) => {
          console.log('error fetching client id:', error);
        });
    }
  }, [hasConfig]);

  useEffect(() => {
    if (user && data && client !== '') {
      if (user.userID && data.environment) {
        configureGATrackingIDsForEnv(client, user.userID, data.environment);
      }
    }
  }, [user, client, data.environment]);

  const fetchConfig = async (): Promise<any> => {
    try {
      const data = await fetchCall(`${process.env.PUBLIC_URL}/config/config.json`);
      return data;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const fetchClientId = async () => {
    try {
      const response = await fetchCall(`${store.getState().config.react_app_api_base_url}/multitenancy/id`, {
        ['hostName']:
          window.location.host === 'localhost:3000' ? 'master.d2fmxqvi3se2z0.amplifyapp.com' : window.location.host
      });
      return response.clientid;
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchConfig()
      .then((data) => {
        store.dispatch(ConfigActions.setConfig(data));
        setHasConfig(true);
      })
      .catch((error) => {
        console.log('error fetching config data:', error);
      });
  }, []);

  // catches axios request and adds proper auth token header
  axios.interceptors.request.use(async (request) => {
    // do not add bearer token to currency/multi-tenancy endpoint path(s) (CORs errors arise)
    if (
      !request.url?.includes('currency') &&
      !request.url?.includes('/multitenancy/id') &&
      !request.url?.includes('config.json')
    ) {
      const accessToken = await oktaAuth.getAccessToken();
      request.headers.Authorization = `Bearer ${accessToken}`;
      request.headers['X-CurrencyContext'] =
        window.location.pathname === '/glance'
          ? store.getState().data.defaultCurrency
          : store.getState().data.selectedCurrency;
    }
    if (
      request.url?.includes('/atglance/sales/summary') ||
      request.url?.includes('/sales/net') ||
      request.url?.includes('/atglance/sales/graph') ||
      request.url?.includes('/scratchpad/multimetric') ||
      (request.url?.includes('/scratchpad/slicer') && request.url?.includes('/netSales'))
    ) {
      request.headers['X-DefaultCurrencyContext'] = store.getState().data.defaultCurrency;
    }

    return request;
  });

  return (
    <PersistGate loading={null} persistor={persistor}>
      {authenticated && (
        <Security oktaAuth={oktaAuth} onAuthRequired={authHandler} restoreOriginalUri={restoreOriginalUri}>
          {/* <LocalStorageChecker /> */}
          {!renewed ? <Initialize /> : ''}
          <React.Fragment>
            <Navigation />
            <Switch>
              <Route path="/login" exact component={Login} />
              <SecureRoute key="overview" exact path="/overview" component={Overview} />,
              <SecureRoute key="overview/sales" exact path="/overview/sales" component={SectionDetail} />,
              <SecureRoute key="/overview/share" exact path="/overview/share" component={SectionDetail} />,
              <SecureRoute
                key="overview/digital-commerce-penetration"
                exact
                path="/overview/digital-commerce-penetration"
                component={SectionDetail}
              />
              ,
              <SecureRoute key="overview/media" exact path="/overview/media" component={SectionDetail} />
              <SecureRoute exact path="/notes" component={Notes} />
              <SecureRoute exact path="/scratchpad" component={ScratchPad} />
              <SecureRoute exact path="/glance" component={Glance} />
              <SecureRoute exact key="upload" path="/account/data-management">
                <DataManagement isMapping={false} />
              </SecureRoute>
              <SecureRoute exact key="mapping" path="/account/data-management/mapping">
                <DataManagement isMapping={true} />
              </SecureRoute>
              <SecureRoute exact path="/no-access" component={NoAccess} />
              <Route path="/login/callback" component={LoginCallback} />
              <Redirect to="/login" />
            </Switch>
          </React.Fragment>
        </Security>
      )}
    </PersistGate>
  );
};

export default AppRouter;
